import { apiDelete, apiGet, apiPatch, apiPost } from "./client";

export type PartyRoleFilter = "all" | "customer" | "supplier";

export type PartyPhone = {
  id: string;
  label: string;
  value: string;
  isPrimary: boolean;
};

export type PartyEmail = {
  id: string;
  label: string;
  value: string;
  isPrimary: boolean;
};

export type PartyAddress = {
  id: string;
  label: string;
  line1: string;
  line2: string | null;
  city: string | null;
  province: string | null;
  postalCode: string | null;
  countryCode: string;
  isPrimary: boolean;
  composed: string;
  mapsUrl: string | null;
};

export type PartyPhonebook = {
  phones: PartyPhone[];
  emails: PartyEmail[];
  addresses: PartyAddress[];
};

export type PartyKind = "org" | "person";

export type PartyPersonLink = {
  id: string;
  label: string;
  personPartyId: string;
  personName: string;
  personKind: PartyKind;
  primaryPhone: string | null;
  primaryEmail: string | null;
};

export type PartyRow = {
  id: string;
  code: string | null;
  name: string;
  /** org (default) | person — plan/48 */
  partyKind?: PartyKind;
  isCustomer: boolean;
  isSupplier: boolean;
  priceListId: string | null;
  /** Durable memo on the contact — not comments (plan/47). */
  notes?: string | null;
  deactivatedAt: string | null;
  updatedAt: string;
  /** Open SO AR when customer; null when supplier-only */
  outstandingAr: string | null;
  /** Open PO AP when supplier; null when customer-only */
  outstandingAp: string | null;
  /** Back-compat: AR, else AP, else "0.00" */
  outstanding: string;
  /** List: primary phone/email when set — plan/47 */
  primaryPhone?: string | null;
  primaryEmail?: string | null;
  /** Present on detail GET — plan/47 */
  phonebook?: PartyPhonebook;
  /** Org detail: linked people — plan/48 */
  people?: PartyPersonLink[];
};

export type PartyListPage = {
  parties: PartyRow[];
  hasMore?: boolean;
  nextCursor?: string | null;
  total?: number;
};

export type PartyListSort = "name" | "code";

export function listParties(
  companyRef: string,
  opts?: {
    role?: PartyRoleFilter;
    kind?: PartyKind;
    includeDeactivated?: boolean;
    q?: string;
    limit?: number;
    cursor?: string | null;
    sort?: PartyListSort;
    sortDir?: "asc" | "desc";
    signal?: AbortSignal;
  },
) {
  const params = new URLSearchParams();
  if (opts?.role && opts.role !== "all") params.set("role", opts.role);
  if (opts?.kind) params.set("kind", opts.kind);
  if (opts?.includeDeactivated) params.set("all", "1");
  if (opts?.q?.trim()) params.set("q", opts.q.trim());
  if (opts?.limit != null) params.set("limit", String(opts.limit));
  if (opts?.cursor) params.set("cursor", opts.cursor);
  if (opts?.sort && opts.sort !== "name") params.set("sort", opts.sort);
  if (opts?.sortDir === "desc") params.set("sortDir", "desc");
  const qs = params.toString();
  return apiGet<PartyListPage>(
    `/c/${companyRef}/parties${qs ? `?${qs}` : ""}`,
    { signal: opts?.signal },
  );
}

export function getParty(companyRef: string, partyId: string) {
  return apiGet<{ party: PartyRow }>(
    `/c/${companyRef}/parties/${partyId}`,
  );
}

export function addPartyPhone(
  companyRef: string,
  partyId: string,
  body: { label?: string; value: string; isPrimary?: boolean },
) {
  return apiPost<{ phone: PartyPhone }>(
    `/c/${companyRef}/parties/${partyId}/phones`,
    body,
  );
}

export function patchPartyPhone(
  companyRef: string,
  partyId: string,
  phoneId: string,
  body: { label?: string; value?: string; isPrimary?: boolean },
) {
  return apiPatch<{ phone: PartyPhone }>(
    `/c/${companyRef}/parties/${partyId}/phones/${phoneId}`,
    body,
  );
}

export function deletePartyPhone(
  companyRef: string,
  partyId: string,
  phoneId: string,
) {
  return apiDelete<{ deleted: boolean }>(
    `/c/${companyRef}/parties/${partyId}/phones/${phoneId}`,
  );
}

export function addPartyEmail(
  companyRef: string,
  partyId: string,
  body: { label?: string; value: string; isPrimary?: boolean },
) {
  return apiPost<{ email: PartyEmail }>(
    `/c/${companyRef}/parties/${partyId}/emails`,
    body,
  );
}

export function patchPartyEmail(
  companyRef: string,
  partyId: string,
  emailId: string,
  body: { label?: string; value?: string; isPrimary?: boolean },
) {
  return apiPatch<{ email: PartyEmail }>(
    `/c/${companyRef}/parties/${partyId}/emails/${emailId}`,
    body,
  );
}

export function deletePartyEmail(
  companyRef: string,
  partyId: string,
  emailId: string,
) {
  return apiDelete<{ deleted: boolean }>(
    `/c/${companyRef}/parties/${partyId}/emails/${emailId}`,
  );
}

export function addPartyAddress(
  companyRef: string,
  partyId: string,
  body: {
    label?: string;
    line1: string;
    line2?: string | null;
    city?: string | null;
    province?: string | null;
    postalCode?: string | null;
    countryCode?: string;
    isPrimary?: boolean;
  },
) {
  return apiPost<{ address: PartyAddress }>(
    `/c/${companyRef}/parties/${partyId}/addresses`,
    body,
  );
}

export function patchPartyAddress(
  companyRef: string,
  partyId: string,
  addressId: string,
  body: {
    label?: string;
    line1?: string;
    line2?: string | null;
    city?: string | null;
    province?: string | null;
    postalCode?: string | null;
    countryCode?: string;
    isPrimary?: boolean;
  },
) {
  return apiPatch<{ address: PartyAddress }>(
    `/c/${companyRef}/parties/${partyId}/addresses/${addressId}`,
    body,
  );
}

export function deletePartyAddress(
  companyRef: string,
  partyId: string,
  addressId: string,
) {
  return apiDelete<{ deleted: boolean }>(
    `/c/${companyRef}/parties/${partyId}/addresses/${addressId}`,
  );
}

export function createParty(
  companyRef: string,
  body: {
    name: string;
    code?: string;
    partyKind?: PartyKind;
    isCustomer?: boolean;
    isSupplier?: boolean;
    priceListId?: string;
  },
) {
  return apiPost<{
    party: {
      id: string;
      name: string;
      partyKind?: PartyKind;
      isCustomer: boolean;
      isSupplier: boolean;
      priceListId: string | null;
    };
  }>(`/c/${companyRef}/parties`, body);
}

export function linkPartyPerson(
  companyRef: string,
  orgPartyId: string,
  body: {
    personPartyId?: string;
    newPerson?: { name: string; code?: string };
    label?: string;
  },
) {
  return apiPost<{
    link: PartyPersonLink | null;
    people: PartyPersonLink[];
  }>(`/c/${companyRef}/parties/${orgPartyId}/people`, body);
}

export function patchPartyPersonLink(
  companyRef: string,
  orgPartyId: string,
  linkId: string,
  body: { label: string },
) {
  return apiPatch<{
    link: PartyPersonLink | null;
    people: PartyPersonLink[];
  }>(`/c/${companyRef}/parties/${orgPartyId}/people/${linkId}`, body);
}

export function unlinkPartyPerson(
  companyRef: string,
  orgPartyId: string,
  linkId: string,
) {
  return apiDelete<{ people: PartyPersonLink[] }>(
    `/c/${companyRef}/parties/${orgPartyId}/people/${linkId}`,
  );
}

export function patchParty(
  companyRef: string,
  partyId: string,
  body: {
    isCustomer?: boolean;
    isSupplier?: boolean;
    priceListId?: string | null;
    notes?: string | null;
    /** Row watermark — plan/38 */
    expectedUpdatedAt?: string;
    /** Before snapshot for patched keys — plan/38 */
    base?: {
      isCustomer?: boolean;
      isSupplier?: boolean;
      priceListId?: string | null;
      notes?: string | null;
    };
  },
) {
  return apiPatch<{
    party: {
      id: string;
      isCustomer: boolean;
      isSupplier: boolean;
      priceListId: string | null;
      notes?: string | null;
      updatedAt?: string;
    };
  }>(`/c/${companyRef}/parties/${partyId}`, body);
}

export type PartyStatement = {
  asOf: string;
  side: "ar" | "ap";
  sideLabel: string;
  party: {
    id: string;
    name: string;
    code: string | null;
    primaryPhone: string | null;
    primaryEmail: string | null;
    primaryAddress: string | null;
  };
  lines: Array<{
    id: string;
    number: string;
    docDate: string;
    paymentDueOn: string | null;
    original: string;
    paid: string;
    balance: string;
    docType: "sales_invoice" | "purchase_invoice";
  }>;
  credits: Array<{
    id: string;
    number: string;
    postedOn: string | null;
    amount: string;
    allocated: string;
    unapplied: string;
  }>;
  openTotal: string;
  creditsTotal: string;
  netDue: string;
  aging: {
    current: string;
    d1_30: string;
    d31_60: string;
    d61_90: string;
    d90_plus: string;
  };
  agingLabels: Record<string, string>;
};

export function getPartyStatement(
  companyRef: string,
  partyId: string,
  opts?: { asOf?: string; side?: "ar" | "ap" },
) {
  const params = new URLSearchParams();
  if (opts?.asOf) params.set("asOf", opts.asOf);
  if (opts?.side) params.set("side", opts.side);
  const qs = params.toString();
  return apiGet<{ statement: PartyStatement }>(
    `/c/${companyRef}/parties/${partyId}/statement${qs ? `?${qs}` : ""}`,
  );
}

export function deactivateParty(companyRef: string, partyId: string) {
  return apiPost<{ party: { id: string; deactivated: boolean } }>(
    `/c/${companyRef}/parties/${partyId}/deactivate`,
  );
}

export function reactivateParty(companyRef: string, partyId: string) {
  return apiPost<{ party: { id: string; deactivated: boolean } }>(
    `/c/${companyRef}/parties/${partyId}/reactivate`,
  );
}
